import React, { useEffect, useState } from 'react'
import ButtonElement from 'pobble-components/dist/button-element'
import { apiClient } from 'pobble-components/dist/api'
import injectSheet from 'react-jss'
import { useSubscriptionDetails } from 'pobble-components/dist/subscriptions/utils'
import { UpsellModal } from './upsell'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCrown } from '@fortawesome/pro-regular-svg-icons/faCrown'
import posthog from 'posthog-js'

const DEFAULT_LEVEL_LABELS = ['Easier', 'Original', 'Harder']

export function useDifficultyLevels({
  presentation,
  includeString,
  levelLabels
}) {
  const [presentations, setPresentations] = useState(null)
  const [selectedLevel, setSelectedLevel] = useState('original') // easier/original/default
  const { hasAccessToTL, hasAccessToPH } = useSubscriptionDetails()

  let isDifferentiated =
    presentation &&
    presentation.easier_version_id &&
    presentation.harder_version_id

  useEffect(
    () => {
      if (
        presentation &&
        presentation.easier_version_id &&
        presentation.harder_version_id
      ) {
        Promise.all([
          apiClient
            .get(`/v3/presentations/${presentation.easier_version_id}`, {
              params: {
                include: includeString
              }
            })
            .then(res => res.data.presentation),
          apiClient
            .get(`/v3/presentations/${presentation.harder_version_id}`, {
              params: {
                include: includeString
              }
            })
            .then(res => res.data.presentation)
        ]).then(setPresentations)
      }
    },
    [presentation, includeString]
  )

  let selectedPresentation = presentation

  if (isDifferentiated && presentations) {
    if (selectedLevel === 'easier') {
      selectedPresentation = {
        ...presentations[0],
        image: presentation.image
      }
    }
    if (selectedLevel === 'harder') {
      selectedPresentation = selectedPresentation = {
        ...presentations[1],
        image: presentation.image
      }
    }
  }

  return {
    selectedPresentation,
    selectedDifficulty: selectedLevel,
    isDifferentiated,
    difficultySelector:
      !isDifferentiated || !presentation ? null : (
        <DifficultySelector
          hasAccess={hasAccessToTL || hasAccessToPH}
          id={presentation.id}
          selected={selectedLevel}
          onChange={setSelectedLevel}
          labels={levelLabels || DEFAULT_LEVEL_LABELS}
        />
      )
  }
}

export const DifficultySelector = injectSheet({
  list: {
    display: 'flex',
    listStyle: 'none',
    margin: '0',
    padding: '0',
    fontWeight: 'bold',
    gap: '16px',
    '& button': {
      color: '#fff',
      textDecoration: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '8px 16px',
      height: '38px',
      width: '106px',
      textAlign: 'center',
      borderRadius: '8px',
      border: '1px solid rgba(255, 255, 255, .3)',
      textShadow: '0 1px 0 rgba(0, 0, 0, .4)',
      '&:hover': {
        background: 'rgba(255, 255, 255, .05)'
      }
    }
  },
  crown: {
    fontSize: '10px',
    marginRight: '6px',
    color: 'var(--yellow)'
  },
  selected: {
    background: 'var(--dark-blue) !important',
    borderColor: 'var(--dark-blue) !important',
    textShadow: 'none !important'
  }
})(({ classes, onSelect, selected, onChange, hasAccess, labels }) => {
  const [upsellModalOpen, setUpsellModalOpen] = useState(false)
  const [easierLabel, originalLabel, harderLabel] = labels

  const idPrefix = hasAccess ? 'tl' : 'free'

  return (
    <>
      <ul className={classes.list}>
        <li>
          <ButtonElement
            id={`diff-easier__${idPrefix}`}
            onClick={() => {
              apiClient.post('/v3/me/activated_with_product', {
                type: 'content'
              })
              posthog.capture('activate', { type: 'content' })
              if (hasAccess) {
                posthog.capture(
                  'click on differentiation button - has access',
                  {
                    level: 'easier'
                  }
                )
                onChange('easier')
              } else {
                posthog.capture('click on differentiation button - no access', {
                  level: 'easier'
                })
                setUpsellModalOpen(true)
              }
            }}
            className={selected === 'easier' ? classes.selected : ''}>
            {!hasAccess && (
              <FontAwesomeIcon className={classes.crown} icon={faCrown} />
            )}
            {easierLabel}
          </ButtonElement>
        </li>
        <li>
          <ButtonElement
            id={`diff-original__${idPrefix}`}
            onClick={() => {
              onChange('original')
            }}
            className={selected === 'original' ? classes.selected : ''}>
            {originalLabel}
          </ButtonElement>
        </li>
        <li>
          <ButtonElement
            id={`diff-harder__${idPrefix}`}
            onClick={() => {
              apiClient.post('/v3/me/activated_with_product', {
                type: 'content'
              })
              posthog.capture('activate', { type: 'content' })
              if (hasAccess) {
                posthog.capture(
                  'click on differentiation button - has access',
                  {
                    level: 'harder'
                  }
                )
                onChange('harder')
              } else {
                posthog.capture('click on differentiation button - no access', {
                  level: 'harder'
                })
                setUpsellModalOpen(true)
              }
            }}
            className={selected === 'harder' ? classes.selected : ''}>
            {!hasAccess && (
              <FontAwesomeIcon className={classes.crown} icon={faCrown} />
            )}
            {harderLabel}
          </ButtonElement>
        </li>
      </ul>
      <UpsellModal
        id="differentiation"
        isOpen={upsellModalOpen}
        requestClose={() => setUpsellModalOpen(false)}
      />
    </>
  )
})
